import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

export interface SkillDocument {
  documentId: string;
  s3Key: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  description?: string;
}

interface PresignedUrlResponse {
  url: string;
  fields: Record<string, string>;
  documentId: string;
  s3Key: string;
}

interface GetPresignedUrlPayload {
  skillId?: string;
  workflowId?: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
}

interface RegisterDocumentPayload {
  skillId?: string;
  workflowId?: string;
  documentId: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
}

interface DeleteDocumentPayload {
  skillId?: string;
  workflowId?: string;
  documentId: string;
}

interface UpdateRequiredInputsPayload {
  skillId?: string;
  workflowId?: string;
  requiredInputsDescription: string;
}

export async function getSkillDocumentPresignedUrl(
  payload: GetPresignedUrlPayload,
): Promise<PresignedUrlResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/skill-documents/presigned-url`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  return json as PresignedUrlResponse;
}

export async function uploadFileToS3(
  presignedUrl: string,
  fields: Record<string, string>,
  file: File,
): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }
  // file must be appended last — AWS stops reading keys after it
  formData.append("file", file, file.name);

  const response = await fetch(presignedUrl, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    let errorMessage = `S3 upload failed with status ${response.status}`;
    try {
      const text = await response.text();
      const messageMatch = text.match(/<Message>(.+?)<\/Message>/)?.[1];
      if (messageMatch) errorMessage = `S3 Error: ${messageMatch}`;
    } catch {
      // ignore parse failures
    }
    throw new Error(errorMessage);
  }
}

export async function registerSkillDocument(
  payload: RegisterDocumentPayload,
): Promise<SkillDocument> {
  const json = await smartFetch(`${API_URL}/private/catalyst/skill-documents`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return json as SkillDocument;
}

export async function deleteSkillDocument(
  payload: DeleteDocumentPayload,
): Promise<void> {
  await smartFetch(`${API_URL}/private/catalyst/skill-documents`, {
    method: "DELETE",
    body: JSON.stringify(payload),
  });
}

export async function updateSkillRequiredInputs(
  payload: UpdateRequiredInputsPayload,
): Promise<void> {
  await smartFetch(`${API_URL}/private/catalyst/skill-required-inputs`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}
